// Social share menu — one-click sharing of achievements and leaderboard
// positions to social platforms.
//
// The shared URL is a truth-backed /share/* landing page (server
// share-pages.js): platforms unfurl its commemorative og-image card, and the
// link itself is the one-click path back to RetroPlay. This module only
// builds URLs/text and presents the menu — the server re-verifies every fact
// against the database, so nothing here is trusted.
//
// Payload shapes accepted by openShareMenu():
//   { kind: 'achievement', username, gameId, achSlug,
//     name, points, gameTitle, rarityPercent?, rarityTier? }
//   { kind: 'score', username, gameId, rank, score, gameTitle }
//   { kind: 'rank', username, rankKind: 'points'|'unlocks', rank, value }

// ── Pure builders (exported for tests) ──────────────────────────────

export function buildSharePath(p) {
  const u = encodeURIComponent(p.username);
  switch (p.kind) {
    case 'achievement':
      return `/share/achievement/${u}/${encodeURIComponent(p.gameId)}/${encodeURIComponent(p.achSlug)}`;
    case 'score':
      return `/share/score/${u}/${encodeURIComponent(p.gameId)}`;
    case 'rank':
      return `/share/rank/${u}/${encodeURIComponent(p.rankKind)}`;
    default:
      throw new Error(`unknown share kind: ${p.kind}`);
  }
}

export function buildShareImagePath(p) {
  return `/api/og-image${buildSharePath(p)}`;
}

export function buildShareText(p) {
  switch (p.kind) {
    case 'achievement': {
      const rarity = (p.rarityTier && p.rarityPercent != null)
        ? `, ${p.rarityTier}, top ${p.rarityPercent}%` : '';
      return `🏆 I unlocked “${p.name}” (${p.points} pts${rarity}) in ${p.gameTitle} on RetroPlay!`;
    }
    case 'score':
      return `🕹️ I'm #${p.rank} on the ${p.gameTitle} leaderboard with ${Number(p.score).toLocaleString()} on RetroPlay!`;
    case 'rank': {
      const what = p.rankKind === 'unlocks' ? 'achievements unlocked' : 'achievement points';
      return `🏅 I'm ranked #${p.rank} on RetroPlay's global leaderboard with ${Number(p.value).toLocaleString()} ${what}!`;
    }
    default:
      throw new Error(`unknown share kind: ${p.kind}`);
  }
}

// Platforms whose web intents accept url and text separately get both;
// text-only intents get "<text> <url>".
export const SHARE_PLATFORMS = [
  { id: 'x',        label: 'X (Twitter)' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'reddit',   label: 'Reddit' },
  { id: 'bluesky',  label: 'Bluesky' },
  { id: 'threads',  label: 'Threads' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'telegram', label: 'Telegram' },
];

export function buildIntentUrl(platformId, url, text) {
  const e = encodeURIComponent;
  switch (platformId) {
    case 'x':        return `https://twitter.com/intent/tweet?text=${e(text)}&url=${e(url)}`;
    case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${e(url)}`;
    case 'reddit':   return `https://www.reddit.com/submit?url=${e(url)}&title=${e(text)}`;
    case 'bluesky':  return `https://bsky.app/intent/compose?text=${e(`${text} ${url}`)}`;
    case 'threads':  return `https://www.threads.net/intent/post?text=${e(`${text} ${url}`)}`;
    case 'whatsapp': return `https://wa.me/?text=${e(`${text} ${url}`)}`;
    case 'telegram': return `https://t.me/share/url?url=${e(url)}&text=${e(text)}`;
    default:         throw new Error(`unknown platform: ${platformId}`);
  }
}

// ── Feature gate ────────────────────────────────────────────────────
// Same launch pattern as suggestFeatureEnabled: one fetch per page load,
// buttons stay hidden until the flag map resolves true.

let _flagsPromise = null;
export function socialSharingEnabled() {
  if (!_flagsPromise) {
    _flagsPromise = fetch('/api/admin/features')
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return _flagsPromise.then(f => f.social_sharing === true);
}

// ── Menu ────────────────────────────────────────────────────────────

let els = null;

function h(tag, attrs = {}, ...children) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  for (const c of children) {
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

function build() {
  if (els) return els;

  const title = h('h2', { class: 'share-menu-title', id: 'share-menu-title' }, 'Share your accomplishment');
  const closeBtn = h('button', { type: 'button', class: 'share-menu-close', 'aria-label': 'Close share menu' }, '×');
  const preview = h('p', { class: 'share-menu-preview' });
  const platformGrid = h('div', { class: 'share-menu-platforms' });
  for (const p of SHARE_PLATFORMS) {
    platformGrid.appendChild(h('a', {
      class: `share-menu-platform share-p-${p.id}`,
      'data-platform': p.id,
      target: '_blank',
      rel: 'noopener noreferrer',
      href: '#',
    }, p.label));
  }

  const nativeBtn = h('button', { type: 'button', class: 'share-menu-native' }, '📲 Share…');
  const copyBtn = h('button', { type: 'button', class: 'share-menu-copy' }, '🔗 Copy link');
  const downloadLink = h('a', { class: 'share-menu-download', href: '#', download: 'retroplay-share.png' }, '⬇ Download image');
  const utilityRow = h('div', { class: 'share-menu-utils' }, nativeBtn, copyBtn, downloadLink);
  const status = h('p', { class: 'share-menu-status', role: 'status', 'aria-live': 'polite' });

  const dialog = h('div', {
    class: 'share-menu-dialog', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'share-menu-title',
  }, closeBtn, title, preview, platformGrid, utilityRow, status);
  const overlay = h('div', { class: 'share-menu-overlay hidden' }, dialog);
  document.body.appendChild(overlay);

  els = { overlay, dialog, title, preview, platformGrid, nativeBtn, copyBtn, downloadLink, status, closeBtn };

  closeBtn.addEventListener('click', closeShareMenu);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeShareMenu(); });
  dialog.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); closeShareMenu(); }
  });

  return els;
}

export function closeShareMenu() {
  if (!els) return;
  els.overlay.classList.add('hidden');
  els.status.textContent = '';
}

/**
 * Open the share menu for a verified accomplishment. See the payload shapes
 * in the module header. The caller is responsible for only offering the menu
 * for the signed-in user's own accomplishments (the server double-checks).
 */
export function openShareMenu(payload) {
  const ui = build();
  const url = `${window.location.origin}${buildSharePath(payload)}`;
  const imagePath = buildShareImagePath(payload);
  const text = buildShareText(payload);

  ui.preview.textContent = text;
  ui.status.textContent = '';

  for (const a of ui.platformGrid.querySelectorAll('a[data-platform]')) {
    a.href = buildIntentUrl(a.dataset.platform, url, text);
    a.setAttribute('aria-label', `Share on ${SHARE_PLATFORMS.find(p => p.id === a.dataset.platform)?.label || a.dataset.platform}`);
  }

  ui.downloadLink.href = imagePath;

  ui.copyBtn.onclick = async () => {
    try {
      await navigator.clipboard.writeText(url);
      ui.status.textContent = 'Link copied!';
    } catch {
      ui.status.textContent = 'Could not copy. Long-press or right-click a platform link instead.';
    }
  };

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  ui.nativeBtn.classList.toggle('hidden', !canNativeShare);
  ui.nativeBtn.onclick = async () => {
    // Attach the card PNG when the platform allows file sharing; otherwise
    // share text + url (the landing page's og-image still supplies the card).
    try {
      if (typeof navigator.canShare === 'function') {
        try {
          const resp = await fetch(imagePath);
          if (resp.ok) {
            const file = new File([await resp.blob()], 'retroplay-share.png', { type: 'image/png' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({ files: [file], text, url });
              return;
            }
          }
        } catch { /* fall through to url share */ }
      }
      await navigator.share({ title: 'RetroPlay', text, url });
    } catch (err) {
      if (err?.name !== 'AbortError') ui.status.textContent = 'Sharing failed. Try Copy link.';
    }
  };

  ui.overlay.classList.remove('hidden');
  ui.closeBtn.focus();
}

/**
 * Small, reusable share trigger for list rows. Hidden until the
 * social_sharing flag resolves true; clicking opens the menu.
 */
export function buildShareButton(payload, { className = 'share-row-btn', label = 'Share' } = {}) {
  const btn = h('button', { type: 'button', class: `${className} hidden`, 'aria-label': label, title: label }, '📤');
  socialSharingEnabled().then((enabled) => { if (enabled) btn.classList.remove('hidden'); });
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    openShareMenu(typeof payload === 'function' ? payload() : payload);
  });
  return btn;
}
